import React from 'react';
import LevelGraph from '../LevelGraph/LevelGraph';
import './StateDetails.scss';
import { marked } from 'marked';

const DETAILS_SUMMARY_STAGES = [
  {
    icon: 'foundation',
    label: 'Readiness',
    levelKey: 'readiness_level',
  },
  {
    icon: 'experiment',
    label: 'Piloting',
    levelKey: 'piloting_level',
  },
  {
    icon: 'build_circle',
    label: 'Implementation',
    levelKey: 'implementation_level',
  },
  {
    icon: 'insights',
    label: 'Impact',
    levelKey: 'impact_level',
  },
];

// Marked (markdown parsing) configurations
const renderer = {
  link(href, title, text) {
    const link = marked.Renderer.prototype.link.call(this, href, title, text);
    return link.replace("<a","<a target='_blank' rel='noreferrer' ");
  }
};
marked.use({
  renderer
});

const parseSources = (sources) => {
  if (!sources || sources.trim() === '') {
    return [];
  }

  const markdownLinks = sources.match(/\[[^\]]+\]\([^\)]+\)/g);
  if (markdownLinks && markdownLinks.length > 0) {
    return markdownLinks.map(item => item.trim());
  }

  const urls = sources.match(/https?:\/\/[^\s,]+/g);
  if (urls && urls.length > 0) {
    return urls.map(item => item.trim());
  }

  if (sources.includes(',')) {
    return sources
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  }

  return [sources.trim()];
};

function capitalizeFirstLetter(str) {
  if (typeof str !== 'string' || str.length === 0) {
    return str;
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const StateDetails = ({ stateData }) => {
  const renderSources = (sources) => {
    const sourceItems = parseSources(sources);
    if (sourceItems.length === 0) {
      return null;
    }

    if (sourceItems.length === 1) {
      return (
        <p className="source" dangerouslySetInnerHTML={{ __html: `Source: ${marked.parseInline(sourceItems[0])}` }} />
      );
    }

    return (
      <div className="source">
        <div>Sources:</div>
        <ul className="ul ul--unstyled">
          {sourceItems.map((sourceItem) => (
            <li key={sourceItem} dangerouslySetInnerHTML={{ __html: marked.parseInline(sourceItem) }} />
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="state-details">
      <div className="container-fluid wrapper stack">
        <div className="row center-md">
          <div className="col-xs-12 col-md-8">
            <div className="state-details__header text-center">
              <h2 className="state-details__title sans-serif">
                { stateData.state_name || stateData.name }
              </h2>
            </div>
          </div>
        </div>
        <div className="row center-md state-details__summary-row">
          {DETAILS_SUMMARY_STAGES.map((stage) => (
            <div className="col-xs-6 col-md-3" key={stage.levelKey}>
              <div className="state-details__summary-dimension">
                <div className="state-details__summary-dimension-copy">
                  <strong className="stage-label state-details__summary-stage">
                    <cfa-icon className="state-details__summary-stage-icon">{stage.icon}</cfa-icon>
                    <span>{stage.label}</span>
                  </strong>
                  <div className="state-details__summary-level">
                    {capitalizeFirstLetter(stateData[stage.levelKey])}
                  </div>
                </div>
                <div className="state-details__summary-dimension-graph">
                  <LevelGraph
                    variant="compact"
                    level={stateData[stage.levelKey]}
                    showLabel={false}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="row stretch-md">
          <div className="col-xs-12 col-md-6">
            <div className="rounded-box">
              <h3 className="sans-serif text-center stage-label">
                <cfa-icon className="stage-icon">foundation</cfa-icon>
                Readiness
              </h3>
              <div className="text-center">
                <LevelGraph level={ stateData.readiness_level?.toLowerCase() } />
              </div>
              <p className="small">{ stateData.readiness_description }</p>
              {renderSources(stateData.readiness_sources)}
            </div>
          </div>
          <div className="col-xs-12 col-md-6">
            <div className="rounded-box">
              <h3 className="sans-serif text-center stage-label">
                <cfa-icon className="stage-icon">experiment</cfa-icon>
                Piloting
              </h3>
              <div className="text-center">
                <LevelGraph level={ stateData.piloting_level?.toLowerCase() } />
              </div>
              <p className="small">{ stateData.piloting_description }</p>
              {renderSources(stateData.piloting_sources)}
            </div>
          </div>
          <div className="col-xs-12 col-md-6">
            <div className="rounded-box">
              <h3 className="sans-serif text-center stage-label">
                <cfa-icon className="stage-icon">build_circle</cfa-icon>
                Implementation
              </h3>
              <div className="text-center">
                <LevelGraph level={ stateData.implementation_level?.toLowerCase() } />
              </div>
              <p className="small">{ stateData.implementation_description }</p>
              {renderSources(stateData.implementation_sources)}
            </div>
          </div>
          <div className="col-xs-12 col-md-6">
            <div className="rounded-box">
              <h3 className="sans-serif text-center stage-label">
                <cfa-icon className="stage-icon">insights</cfa-icon>
                Impact
              </h3>
              <div className="text-center">
                <LevelGraph level={ stateData.impact_level?.toLowerCase() } />
              </div>
              <p className="small">{ stateData.impact_description }</p>
              {renderSources(stateData.impact_sources)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StateDetails;
